import { Component, DestroyRef, OnDestroy, computed, inject } from '@angular/core';
import { ReportsAsync } from '../../store/reports/async';
import { ICustomGroupReportReq, ICustomGroupReportRes, IReportsState } from '../../store/reports/state';
import { Observable } from 'rxjs';
import { getSelectedAdminPlan } from '../../login/store/selectors';
import { ILoginState } from '../../login/store/state';
import { errorGetCustomGroupReport, errorGetDocument, getCustomGroupReportRes, isFetchingCustomGroupReport } from '../../store/reports/selector';
import { CustomGroupReportRequestPayload } from 'src/app/reports/custom-group-report/custom-group-report.component';
import { initialReportsState } from 'src/app/store/reports/initialState';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

type SelectedReportRequest = {
  id: string | number;
  type: string;
};

@Component({
    selector: 'bmt-custom-group-report',
    template: `
      <app-custom-group-report-page
        [selectedAdminPlan]="selectedAdminPlan()"
        [isFetching]="isFetchingCustomGroupReport()"
        [customGroupReports]="getCustomGroupReportRes()"
        [customGroupError]="customGroupErrorMessage()"
        (getCustomGroupReport)="getCustomGroupReport($event)"
        (selectedReport)="getGroupReport($event)"
        (resetReport)="resetReport()"></app-custom-group-report-page>
    `,
    standalone: false
})
export class CustomGroupReportContainer implements OnDestroy {
  readonly luxId = 'bmt-custom-group-report' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  readonly customGroupError = toSignal(this.lux.get(errorGetCustomGroupReport, this.luxId), { initialValue: '' });
  readonly errorGetDocument = toSignal(this.lux.get(errorGetDocument, this.luxId), { initialValue: '' });
  readonly getCustomGroupReportRes = toSignal(this.lux.get(getCustomGroupReportRes, this.luxId), { initialValue: undefined as ICustomGroupReportRes | undefined });
  readonly isFetchingCustomGroupReport = toSignal(this.lux.get(isFetchingCustomGroupReport, this.luxId), { initialValue: false });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly customGroupErrorMessage = computed(() => this.customGroupError() || this.errorGetDocument());

  constructor(
    private readonly reportsAsync: ReportsAsync,
    private readonly lux: Lux<{ reportsState: IReportsState, login: ILoginState }>
  ) {
  }

  getCustomGroupReport(value: CustomGroupReportRequestPayload): void {
    const req: ICustomGroupReportReq = {
      planAcronym: value.planAcronym,
      clientSpecifiedId: value.clientSpecifiedId,
      subClientSpecifiedId: value.subClientSpecifiedId,
      fromDate: value.formValue.fromDate,
      toDate: value.formValue.toDate,
      frequency: value.formValue.frequency,
      pagination: {
        limit: 25,
        offset: value.offset
      },
      sort: [{
        order: 'DESC',
        fieldName: 'toDate'
      }]
    };
    this.lux.set(state => state.reportsState.customGroupReportReq, req);
    this.runRequest(this.reportsAsync.getCustomGroupReports());
  }

  getGroupReport(req: SelectedReportRequest): void {
    if (req?.type === 'PDF') {
      this.runRequest(this.reportsAsync.getPDFDocument(req));
      return;
    }
    this.runRequest(this.reportsAsync.getExcelDocument(req));
  }

  private runRequest(request$: Observable<unknown>): void {
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  resetReport(): void {
    this.lux.set(state => state.reportsState, initialReportsState);
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }
}
